import { Component, OnDestroy, OnInit, computed, signal, WritableSignal } from '@angular/core';
import { FormControl } from '@angular/forms';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { AppSettings } from '../../../../services/settings/app-settings.service';
import { DialogService } from 'primeng/dynamicdialog';
import { ConfigureUserDialogComponent } from './components/configure-user-dialog/configure-user-dialog.component';
import { Users } from '../../../../interfaces/users';
import { CoreService } from '../../../../services/core/core.service';
import { MessageService } from 'primeng/api';
import { canonicalServiceId, CLIENT_PRODUCT_SERVICE_IDS } from '../../../../shared/service-aliases';

/** Opciones de filtro para la tabla */
export interface UserFilters {
  roles: string[];
  statuses: string[];
  services: string[];
}

@Component({
  selector: 'app-user-management',
  standalone: false,
  templateUrl: './user-management.component.html',
  styleUrl: './user-management.component.scss',
  providers: [DialogService, MessageService],
})
export class UserManagementComponent implements OnInit, OnDestroy {
  countOfUsers: WritableSignal<number | null> = signal(null);
  listOfOrganizations: WritableSignal<any[]> = signal([]);
  fullListOfUsers: WritableSignal<Users[]> = signal([]);
  loadingUsers = signal(true);
  // Búsqueda
  searchControl = new FormControl('');
  private searchQuery = signal('');

  // Filtros
  showFilterSidebar = signal(false);
  activeFilters = signal<UserFilters>({ roles: [], statuses: [], services: [] });
  activeFilterCount = computed(() => {
    const f = this.activeFilters();
    return f.roles.length + f.statuses.length + f.services.length;
  });

  // Opciones de filtros disponibles (se derivan de los datos)
  availableRoles = computed(() => {
    const users = this.fullListOfUsers();
    const set = new Set<string>();
    users.forEach((u: any) => {
      const m = this.getCurrentMembership(u);
      if (m?.bundle_name) set.add(m.bundle_name);
      else if (m?.role) set.add(m.role);
    });
    return Array.from(set).sort();
  });
  availableServices = computed(() => {
    const users = this.fullListOfUsers();
    const set = new Set<string>();
    users.forEach((u: any) => {
      const m = this.getCurrentMembership(u);
      const svcs: string[] = Array.isArray(m?.available_services) ? m!.available_services : [];
      svcs.forEach((s: string) => {
        // El servicio guardado puede traer el id anterior del producto: se agrupa por el vigente.
        const canonico = canonicalServiceId(s);
        if (CLIENT_PRODUCT_SERVICE_IDS.includes(canonico)) set.add(canonico);
      });
      // Derivar de roles si available_services vacío
      if (svcs.length === 0 && Array.isArray(m?.roles)) {
        for (const r of m!.roles) {
          const svc = canonicalServiceId(String(r).split('_')[0]);
          if (CLIENT_PRODUCT_SERVICE_IDS.includes(svc)) set.add(svc);
        }
      }
    });
    return Array.from(set).sort();
  });

  readonly statusOptions = [
    { label: 'Habilitado', value: 'active' },
    { label: 'Deshabilitado', value: 'inactive' },
    { label: 'Invitado', value: 'invited' },
  ];
  readonly serviceDisplayNames: Record<string, string> = {
    lelfunko: 'Lelfunko',
    airnode: 'Airnode',
    birdattack: 'BirdAttack',
  };

  // Filtros temporales (dentro del sidebar)
  filterRoles = signal<string[]>([]);
  filterStatuses = signal<string[]>([]);
  filterServices = signal<string[]>([]);

  filteredUsers = computed(() => {
    const query = (this.searchQuery() || '').trim().toLowerCase();
    const filters = this.activeFilters();
    let users = this.fullListOfUsers();

    // Búsqueda por texto
    if (query) {
      users = users.filter((u: any) => (u?.email || '').toLowerCase().includes(query));
    }

    // Filtro por rol
    if (filters.roles.length > 0) {
      users = users.filter((u: any) => {
        const m = this.getCurrentMembership(u);
        const role = m?.bundle_name || m?.role || '';
        return filters.roles.includes(role);
      });
    }

    // Filtro por estado
    if (filters.statuses.length > 0) {
      users = users.filter((u: any) => {
        const status = this.getUserStatus(u);
        return filters.statuses.includes(status);
      });
    }

    // Filtro por servicio
    if (filters.services.length > 0) {
      users = users.filter((u: any) => {
        const m = this.getCurrentMembership(u);
        const svcs = this.getUserServices(m);
        return filters.services.some((f: string) => svcs.includes(f));
      });
    }

    return users;
  });

  pageSize: number = 5;
  lastEvaluatedKey: string | undefined = undefined;

  constructor(
    private appSettings: AppSettings,
    private dialog: DialogService,
    private readonly coreService: CoreService,
    private messageService: MessageService
  ) {
    this.appSettings.appHideGlobalHeader.set(false);
  }

  async ngOnInit(): Promise<void> {
    try {
      // Primero aseguramos que la organización actual esté sincronizada
      // con las organizaciones reales del usuario en backend.
      await this.coreService.ensureOrganizationFromBackend();

      // Una vez conocida la organización (o la primera disponible),
      // cargamos contadores, usuarios y organizaciones para la UI.
      this.getTotalAmountOfUsers();
      this.loadUsers();
      this.loadOrganizations();
    } finally {
      // Suscribir cambios del buscador (independiente de la carga inicial)
      this.searchControl.valueChanges.pipe(debounceTime(200), distinctUntilChanged()).subscribe((value) => this.searchQuery.set((value || '').toString()));
    }
  }

  loadUsers() {
    this.loadingUsers.set(true);
    this.coreService
      .listOrganizationUsers(this.lastEvaluatedKey, this.pageSize)
      .then((data) => {
        const current = this.fullListOfUsers();
        const users = data?.users ?? [];
        this.fullListOfUsers.set([...current, ...users]);
        this.lastEvaluatedKey = data?.lastEvaluatedKey;
        // Si viene totalCount desde el listado, úsalo para el encabezado
        if (typeof data?.totalCount === 'number') {
          this.countOfUsers.set(data.totalCount);
        }
        this.loadingUsers.set(false);
      })
      .catch((error) => {
        console.error('Failed to load users:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Error al cargar usuarios',
          detail: error?.message || 'Ocurrió un error inesperado cargando usuarios.',
          life: 5000,
        });
        this.loadingUsers.set(false);
      });
  }

  loadOrganizations() {
    this.coreService
      .listOrganizationsForCurrentUser()
      .then((data) => {
        this.listOfOrganizations.set(data);
      })
      .catch((error) => {
        console.error('Failed to load organizations:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Error al cargar organizaciones',
          detail: error?.message || 'Ocurrió un error inesperado cargando organizaciones.',
          life: 5000,
        });
      });
  }

  getTotalAmountOfUsers() {
    this.coreService
      .getTotalAmountOfUsers()
      .then((data) => {
        const totalCandidate = typeof data === 'number' ? data : (data?.totalCount ?? data?.count ?? data?.total);
        if (totalCandidate === undefined || totalCandidate === null) {
          this.countOfUsers.set(null);
          return;
        }
        const numericTotal = Number(totalCandidate);
        this.countOfUsers.set(Number.isFinite(numericTotal) ? numericTotal : null);
      })
      .catch((error) => {
        console.error('Failed to get total amount of users:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Error al obtener total de usuarios',
          detail: error?.message || 'No fue posible obtener el total de usuarios.',
          life: 5000,
        });
        this.countOfUsers.set(null);
      });
  }

  configureProfileDialog() {
    const ref = this.dialog.open(ConfigureUserDialogComponent, {
      header: 'Añadir Usuario',
      width: '50vw',
      contentStyle: { overflow: 'visible' },
      breakpoints: {
        '960px': '75vw',
        '640px': '90vw',
      },
      data: {
        isNew: true,
        user: null,
        organizations: this.listOfOrganizations(),
        organizationId: this.coreService.resolveCurrentOrganizationId(),
      },
    });

    ref?.onClose.subscribe((data: any) => {
      if (data?.status) {
        this.messageService.add({
          severity: data.severity,
          summary: data.summary,
          detail: data.detail ?? data.details,
          life: data.life,
        });
      }
      // Si el diálogo reporta éxito, recargar la tabla desde el inicio
      if (data?.created === true || data?.status === 'success') {
        this.resetAndReload();
      }
    });
  }

  ngOnDestroy(): void {
    this.appSettings.appEmpty.set(true);
    this.appSettings.appHideGlobalHeader.set(true);
  }

  // === Helpers ===
  onDataChanged(): void {
    this.resetAndReload();
  }

  onToastMessage(event: { severity: string; summary: string; detail: string; life?: number }): void {
    this.messageService.add(event);
  }

  /** Obtiene la membresía del usuario en la organización actual */
  private getCurrentMembership(user: any): any {
    const orgId = this.coreService.resolveCurrentOrganizationId();
    if (!orgId || !user?.membership) return null;
    if (Array.isArray(user.membership)) {
      return user.membership.find((m: any) => m.organization_id === orgId || m.orgId === orgId);
    }
    return user.membership[orgId];
  }

  /** Resuelve estado del usuario (simplificado para filtros) */
  private getUserStatus(user: any): string {
    const rootStatus = (user?.status || user?.user_status || '').toString().toLowerCase();
    if (rootStatus === 'invited' || rootStatus === 'pending') return 'invited';
    if (rootStatus === 'active') return 'active';
    if (rootStatus === 'inactive') return 'inactive';
    const m = this.getCurrentMembership(user);
    const s = (m?.status || '').toString().toLowerCase();
    if (s === 'invited' || s === 'pending') return 'invited';
    if (s === 'active') return 'active';
    return 'inactive';
  }

  /** Obtiene servicios de producto del usuario */
  private getUserServices(membership: any): string[] {
    if (!membership) return [];
    const svcs: string[] = Array.isArray(membership.available_services) ? membership.available_services : [];
    const result = svcs.map((s: string) => canonicalServiceId(s)).filter((s: string) => CLIENT_PRODUCT_SERVICE_IDS.includes(s));
    // Derivar de roles si vacío
    if (result.length === 0 && Array.isArray(membership.roles)) {
      for (const r of membership.roles) {
        // El rol guardado puede llevar el nombre anterior del producto (`qsense_admin`).
        const svc = canonicalServiceId(String(r).split('_')[0]);
        if (CLIENT_PRODUCT_SERVICE_IDS.includes(svc) && !result.includes(svc)) result.push(svc);
      }
    }
    return result;
  }

  // === Sidebar de filtros ===
  openFilterSidebar(): void {
    // Cargar filtros activos en los temporales
    const f = this.activeFilters();
    this.filterRoles.set([...f.roles]);
    this.filterStatuses.set([...f.statuses]);
    this.filterServices.set([...f.services]);
    this.showFilterSidebar.set(true);
  }

  applyFilters(): void {
    this.activeFilters.set({
      roles: [...this.filterRoles()],
      statuses: [...this.filterStatuses()],
      services: [...this.filterServices()],
    });
    this.showFilterSidebar.set(false);
  }

  clearFilters(): void {
    this.filterRoles.set([]);
    this.filterStatuses.set([]);
    this.filterServices.set([]);
    this.activeFilters.set({ roles: [], statuses: [], services: [] });
    this.showFilterSidebar.set(false);
  }

  toggleFilterRole(role: string): void {
    const current = this.filterRoles();
    if (current.includes(role)) {
      this.filterRoles.set(current.filter((r) => r !== role));
    } else {
      this.filterRoles.set([...current, role]);
    }
  }

  toggleFilterStatus(status: string): void {
    const current = this.filterStatuses();
    if (current.includes(status)) {
      this.filterStatuses.set(current.filter((s) => s !== status));
    } else {
      this.filterStatuses.set([...current, status]);
    }
  }

  toggleFilterService(service: string): void {
    const current = this.filterServices();
    if (current.includes(service)) {
      this.filterServices.set(current.filter((s) => s !== service));
    } else {
      this.filterServices.set([...current, service]);
    }
  }

  private resetAndReload(): void {
    this.fullListOfUsers.set([]);
    this.lastEvaluatedKey = undefined;
    this.getTotalAmountOfUsers();
    this.loadUsers();
  }
}
